import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Loader } from "../feedback/loader";
import { Modal } from "./modal";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title: string;
  dimensions?: { width: number; height: number };
}

export function VideoModal({
  isOpen,
  onClose,
  videoUrl,
  title,
  dimensions,
}: VideoModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // Reset loading state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsVideoLoading(true);
    }
  }, [isOpen]);

  // Autoplay when modal opens, pause when it closes
  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked - user can manually play
      });
    } else if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isOpen]);

  const aspectRatio =
    dimensions?.width && dimensions?.height
      ? dimensions.width / dimensions.height
      : 16 / 9;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="relative rounded-lg overflow-hidden bg-black border border-white/10">
        {/* Loading state with correct aspect ratio */}
        {isVideoLoading && (
          <div
            className="flex items-center justify-center bg-black"
            style={{ aspectRatio }}
          >
            <Loader />
          </div>
        )}
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          autoPlay
          loop
          playsInline
          preload="auto"
          onLoadedData={() => setIsVideoLoading(false)}
          className={cn(
            "w-full max-h-[60vh] object-contain",
            isVideoLoading && "hidden",
          )}
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </Modal>
  );
}
